import React from "react";
import {
  Document,
  Font,
  Image,
  Page,
  Path,
  StyleSheet,
  Svg,
  Text,
  View,
  pdf,
} from "@react-pdf/renderer";
import { SolutionCertificateAPIData } from "../../../../../../../../redux/slices/validation-documentation/Types";
import topBackground from "../../../../../../../../common/assets/images/certificate/hid-partner-solution-certificate-background-top-image.jpg";
import bottomBackground from "../../../../../../../../common/assets/images/certificate/hid-partner-solution-certificate-background-bottom-image.jpg";
import hidLogo from "../../../../../../../../common/assets/images/certificate/hid-pti-logo-reverse-q4-2026.png";
import neueHaasRegular from "../../../../../../../../common/assets/fonts/neue-haas-unica/NeueHaasUnicaPro-Regular.otf";
import neueHaasMedium from "../../../../../../../../common/assets/fonts/neue-haas-unica/NeueHaasUnicaPro-Medium.otf";
import neueHaasBold from "../../../../../../../../common/assets/fonts/neue-haas-unica/NeueHaasUnicaPro-Bold.otf";

export interface SolutionCertificateData {
  companyName: string;
  applicationName: string;
  applicationVersion: string;
  technology: string;
  integrationType: string;
  certificationStatus: string;
  endorsementLevel: string;
  endorsementMessage: string;
  coverage: {
    mandatory: number;
    recommended: number;
    bestPractices: number;
    optional: number;
  };
  features: { name: string; implemented: boolean }[];
  validatedDate: string;
  validatedBy: string;
}

const EMPTY_VALUE = "-";

const textOrDash = (value: string | number | null | undefined) =>
  value === null || value === undefined || String(value).trim() === ""
    ? EMPTY_VALUE
    : String(value);

export const mapSolutionCertificateResponse = (
  apiData: SolutionCertificateAPIData,
  companyName: string
): SolutionCertificateData => ({
  companyName: textOrDash(companyName),
  applicationName: textOrDash(apiData.application_name),
  applicationVersion: textOrDash(apiData.application_version),
  technology: textOrDash(apiData.technology_name),
  integrationType: textOrDash(apiData.integration_name),
  certificationStatus: textOrDash(apiData.certification_status),
  endorsementLevel: textOrDash(apiData.endorsement_level),
  endorsementMessage: textOrDash(apiData.endorsement_message),
  coverage: {
    mandatory: apiData.scores?.mandatory ?? 0,
    recommended: apiData.scores?.recommended ?? 0,
    bestPractices: apiData.scores?.bestPractice ?? 0,
    optional: apiData.scores?.optional ?? 0,
  },
  features: (apiData.features ?? []).flatMap((category) =>
    (category.features ?? []).map((feature) => ({
      name: feature.feature_name,
      implemented: true,
    }))
  ),
  validatedDate: textOrDash(apiData.verified_date),
  validatedBy: textOrDash(apiData.validated_by),
});

const FONT_FAMILY = "NeueHaasUnica";

Font.register({
  family: FONT_FAMILY,
  fonts: [
    { src: neueHaasRegular, fontWeight: 400 },
    { src: neueHaasMedium, fontWeight: 500 },
    { src: neueHaasBold, fontWeight: 700 },
  ],
});

Font.registerHyphenationCallback((word) => [word]);

const NAVY = "#102C53";
const BODY = "#000";
const LABEL = "#3E5878";
const VALUE = "#4E5963";
const PANEL = "#EEEFF1";
const DIVIDER = "#5C5C5C";
const WHITE = "#FFFFFF";

const PAGE_WIDTH = 841.89;
const PAGE_PADDING_X = 35;
const PAGE_PADDING_Y = 0;
const FOOTER_EXTRA_HEIGHT = 15;
const STATUS_GAP = 24.5;
const PAGE_HEIGHT = 600 + FOOTER_EXTRA_HEIGHT + 20;
const CONTENT_WIDTH = PAGE_WIDTH - PAGE_PADDING_X * 2;
const SECTION_INSET = 30;
const TOP_BANNER_HEIGHT = CONTENT_WIDTH * (558 / 3208);
const BOTTOM_BANNER_HEIGHT = CONTENT_WIDTH * (251 / 3208) + FOOTER_EXTRA_HEIGHT;
const LOGO_WIDTH = 130;
const LOGO_HEIGHT = LOGO_WIDTH * (100 / 608);

const styles = StyleSheet.create({
  page: {
    paddingTop: PAGE_PADDING_Y,
    paddingBottom: PAGE_PADDING_Y,
    paddingHorizontal: PAGE_PADDING_X,
    backgroundColor: WHITE,
    fontFamily: FONT_FAMILY,
    fontWeight: 400,
  },
  banner: {
    width: CONTENT_WIDTH,
    height: TOP_BANNER_HEIGHT,
  },
  bannerImage: {
    width: CONTENT_WIDTH,
    height: TOP_BANNER_HEIGHT,
  },
  bannerOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: CONTENT_WIDTH,
    height: TOP_BANNER_HEIGHT,
  },
  title: {
    position: "absolute",
    left: 40,
    top: TOP_BANNER_HEIGHT / 2 - 23,
    fontFamily: FONT_FAMILY,
    fontWeight: 700,
    fontSize: 38,
    color: WHITE,
  },
  logo: {
    position: "absolute",
    top: 14,
    right: 22,
    width: LOGO_WIDTH,
    height: LOGO_HEIGHT,
  },
  intro: {
    marginTop: 18,
    marginHorizontal: SECTION_INSET,
    paddingHorizontal: 20,
  },
  introLine: {
    fontSize: 15,
    fontWeight: 400,
    color: NAVY,
    lineHeight: 1.35,
    textAlign: "center",
  },
  companyName: {
    fontFamily: FONT_FAMILY,
    fontWeight: 700,
    fontSize: 15,
    color: NAVY,
  },
  certificationStatus: {
    marginHorizontal: SECTION_INSET,
    alignItems: "center",
  },
  certificationLine: {
    fontSize: 18,
    fontWeight: 400,
    color: NAVY,
    textAlign: "center",
  },
  certificationValue: {
    fontFamily: FONT_FAMILY,
    fontWeight: 700,
    fontSize: 18,
    color: "#000",
  },
  detailsCard: {
    marginTop: 0,
    marginHorizontal: SECTION_INSET,
    borderWidth: 1.2,
    borderColor: NAVY,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
  },
  detailsHeader: {
    backgroundColor: NAVY,
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 5,
    paddingHorizontal: 14,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  detailsHeaderText: {
    fontFamily: FONT_FAMILY,
    fontWeight: 700,
    fontSize: 12,
    color: WHITE,
    marginLeft: 10,
  },
  detailsBody: {
    flexDirection: "row",
    backgroundColor: PANEL,
    minHeight: 65,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
  detailColumn: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 14,
    justifyContent: "flex-start",
  },
  detailLabel: {
    fontSize: 12,
    color: LABEL,
    fontWeight: 500,
    marginBottom: -1,
  },
  detailDivider: {
    width: 1,
    backgroundColor: DIVIDER,
    marginVertical: 12,
  },
  detailValue: {
    fontSize: 14,
    color: "#000000",
    fontWeight: 500,
    lineHeight: 1.3,
  },
  endorsementBox: {
    marginTop: 16,
    marginHorizontal: SECTION_INSET,
    borderWidth: 1.2,
    borderColor: BODY,
    borderRadius: 12,
    backgroundColor: PANEL,
    paddingVertical: 8,
    paddingHorizontal: 20,
    alignItems: "center",
  },
  endorsementHalf: {
    width: "100%",
    minHeight: 25,
    justifyContent: "center",
    alignItems: "center",
  },
  endorsementLine: {
    fontSize: 14,
    fontWeight: 400,
    color: NAVY,
    textAlign: "center",
    paddingBottom: 10,
  },
  endorsementValue: {
    fontFamily: FONT_FAMILY,
    fontWeight: 500,
    fontSize: 15,
    color: BODY,
  },
  endorsementDivider: {
    height: 1,
    width: "98%",
    backgroundColor: "#C5CDD6",
  },
  endorsementNote: {
    fontSize: 13,
    color: NAVY,
    textAlign: "center",
    lineHeight: 1.50,
    paddingTop: 10,
  },
  statusGap: {
    height: STATUS_GAP,
  },
  footerSpacer: {
    height: 20,
  },
  footer: {
    width: CONTENT_WIDTH,
    height: BOTTOM_BANNER_HEIGHT,
  },
  footerImage: {
    width: CONTENT_WIDTH,
    height: BOTTOM_BANNER_HEIGHT,
  },
  footerOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: CONTENT_WIDTH,
    height: BOTTOM_BANNER_HEIGHT,
  },
  footerLeft: {
    position: "absolute",
    left: 40,
    top: 0,
    height: BOTTOM_BANNER_HEIGHT,
    justifyContent: "center",
  },
  footerRight: {
    position: "absolute",
    right: 40,
    top: 0,
    height: BOTTOM_BANNER_HEIGHT,
    justifyContent: "center",
    alignItems: "flex-start",
  },
  footerLabel: {
    fontSize: 8,
    color: WHITE,
    marginBottom: -2,
    textAlign: "left",
  },
  footerValue: {
    fontSize: 10.5,
    color: WHITE,
    fontWeight: 400,
  },
});

const SolutionDetailsIcon = () => (
  <Svg width={12} height={14} viewBox="0 0 12 14">
    <Path
      d="M1.8 0.7 H6.6 L10.2 4.1 V12.2 C10.2 12.7 9.8 13.2 9.2 13.2 H1.8 C1.2 13.2 0.8 12.7 0.8 12.2 V1.7 C0.8 1.1 1.2 0.7 1.8 0.7 Z"
      stroke={WHITE}
      strokeWidth={1}
      fill="none"
    />
    <Path d="M6.5 0.8 V4.2 H10" stroke={WHITE} strokeWidth={1} fill="none" />
    <Path
      d="M2.8 6.8 H8.2 M2.8 8.7 H8.2 M2.8 10.6 H6"
      stroke={WHITE}
      strokeWidth={0.9}
      strokeLinecap="round"
    />
  </Svg>
);

const DetailColumn = ({
  label,
  value,
  showDivider,
}: {
  label: string;
  value: string;
  showDivider: boolean;
}) => (
  <>
    <View style={styles.detailColumn}>
      <Text style={styles.detailLabel}>{label}</Text>
      <Text style={styles.detailValue}>{value}</Text>
    </View>
    {showDivider ? <View style={styles.detailDivider} /> : null}
  </>
);

export const SolutionCertificatePDF = ({
  data,
}: {
  data: SolutionCertificateData;
}) => {
  const details = [
    { label: "Application Name", value: data.applicationName },
    { label: "Application Version", value: data.applicationVersion },
    { label: "Technology", value: data.technology },
    { label: "Integration Type", value: data.integrationType },
  ];
  const note =
    data.endorsementMessage && data.endorsementMessage !== "-"
      ? data.endorsementMessage
      : "";

  return (
    <Document title="HID Solution Certificate">
      <Page size={[PAGE_WIDTH, PAGE_HEIGHT]} style={styles.page} wrap={false}>
        <View style={styles.banner}>
          <Image src={topBackground} style={styles.bannerImage} />
          <View style={styles.bannerOverlay}>
            <Text style={styles.title}>HID Solution Certificate</Text>
            <Image src={hidLogo} style={styles.logo} />
          </View>
        </View>

        <View style={styles.intro}>
          <Text style={styles.introLine}>
            This Certificate Recognizes That{" "}
            <Text style={styles.companyName}>{data.companyName}</Text>
            {" "}
            Has Successfully Completed the HID Solution Validation Process for the
            following Solutions.
          </Text>
        </View>

        <View style={styles.statusGap} />

        <View style={styles.certificationStatus}>
          <Text style={styles.certificationLine}>
            Certification Status:{" "}
            <Text style={styles.certificationValue}>{data.certificationStatus}</Text>
          </Text>
        </View>

        <View style={styles.statusGap} />

        <View style={styles.detailsCard}>
          <View style={styles.detailsHeader}>
            <SolutionDetailsIcon />
            <Text style={styles.detailsHeaderText}>Solution Details</Text>
          </View>
          <View style={styles.detailsBody}>
            {details.map((detail, index) => (
              <DetailColumn
                key={detail.label}
                label={detail.label}
                value={detail.value}
                showDivider={index < details.length - 1}
              />
            ))}
          </View>
        </View>

        <View style={styles.endorsementBox}>
          <View style={styles.endorsementHalf}>
            <Text style={styles.endorsementLine}>
              Endorsement Status:{" "}
              <Text style={styles.endorsementValue}>{data.endorsementLevel}</Text>
            </Text>
          </View>
          {note ? (
            <>
              <View style={styles.endorsementDivider} />
              <View style={styles.endorsementHalf}>
                <Text style={styles.endorsementNote}>{note}</Text>
              </View>
            </>
          ) : null}
        </View>

        <View style={styles.footerSpacer} />

        <View style={styles.footer}>
          <Image src={bottomBackground} style={styles.footerImage} />
          <View style={styles.footerOverlay}>
            <View style={styles.footerLeft}>
              <Text style={styles.footerLabel}>Validated Date</Text>
              <Text style={styles.footerValue}>{data.validatedDate}</Text>
            </View>
            <View style={styles.footerRight}>
              <Text style={styles.footerLabel}>Validated By:</Text>
              <Text style={styles.footerValue}>{data.validatedBy}</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

const PDF_FILENAME = "HID-Solution-Certificate.pdf";

type PdfWindow = Window & {
  File: typeof File;
  URL: typeof URL;
};

/**
 * Chrome's PDF viewer re-fetches the file when Save is clicked. Navigating the tab
 * to a blob URL unloads the document that owns it, so that refetch fails with
 * "Couldn't download - Network issue". Keep the owning document open and embed the PDF.
 */
const mountPdfViewer = (tab: Window, pdfBlob: Blob) => {
  const targetWindow = tab as PdfWindow;
  const doc = tab.document;
  doc.open();
  doc.write(`<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>${PDF_FILENAME}</title>
<style>
  html, body { margin: 0; height: 100%; overflow: hidden; background: #525659; }
  embed { display: block; border: 0; width: 100%; height: 100%; }
</style>
</head>
<body>
  <embed id="hid-solution-certificate" type="application/pdf" />
</body>
</html>`);
  doc.close();

  const file = new targetWindow.File([pdfBlob], PDF_FILENAME, {
    type: "application/pdf",
  });
  const url = targetWindow.URL.createObjectURL(file);
  doc.getElementById("hid-solution-certificate")?.setAttribute("src", url);
};

const openPdfBlob = async (blob: Blob, targetTab: Window | null) => {
  if (targetTab && !targetTab.closed) {
    mountPdfViewer(targetTab, blob);
    return;
  }

  const opened = window.open("", "_blank");
  if (opened) {
    mountPdfViewer(opened, blob);
    return;
  }

  const file = new File([blob], PDF_FILENAME, { type: "application/pdf" });
  const url = URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = url;
  link.download = PDF_FILENAME;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

/**
 * Renders the certificate PDF into `targetTab`, where the browser viewer handles view/download.
 * `targetTab` must be opened synchronously inside the click handler, otherwise popup blockers
 * reject it once the async work below has run.
 */
export const openSolutionCertificateInTab = async (
  data: SolutionCertificateData,
  targetTab: Window | null
) => {
  const blob = await pdf(<SolutionCertificatePDF data={data} />).toBlob();
  await openPdfBlob(blob, targetTab);
};
